import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  MapPin,
  RefreshCw,
  Ticket,
  TrendingUp,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fetchOperational } from "./api";
import type { OperationalData, NextEventInfo } from "./types";
import { fmtCLP, fmtInt } from "./format";
import { LoadingBlock, ErrorBlock, EmptyBlock } from "./shared/StateBlocks";

export function OperationalScreen() {
  const query = useQuery({
    queryKey: ["dashboard", "operational"],
    queryFn: fetchOperational,
    refetchInterval: 60_000,
  });

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Dashboard · Operativo</p>
          <h1 className="nx-h1">Estado del día</h1>
          <p className="text-sm text-fg-muted mt-1">
            Próximo evento, ventas de hoy, validaciones en puerta y alertas activas. Refresca cada 60s.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          className="nx-btn-secondary"
          disabled={query.isFetching}
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando dashboard operativo…" />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <DashboardBody data={query.data} />
      )}
    </div>
  );
}

function DashboardBody({ data }: { data: OperationalData }) {
  return (
    <>
      <HeroNextEvent ev={data.nextEvent} />

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TodayCard
          icon={<TrendingUp size={16} />}
          label="Ingresos hoy"
          value={fmtCLP(data.today.revenue)}
        />
        <TodayCard
          icon={<Ticket size={16} />}
          label="Tickets vendidos hoy"
          value={fmtInt(data.today.ticketsSold)}
        />
        <TodayCard
          icon={<CheckCircle2 size={16} />}
          label="Validados en puerta hoy"
          value={fmtInt(data.today.validated)}
        />
      </section>

      <section className="nx-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="nx-h2">Validaciones · últimas 12h</h2>
          <span className="text-2xs text-fg-subtle">por hora</span>
        </div>
        <ValidationsChart data={data.validationsByHour} />
      </section>

      <section>
        <h2 className="nx-h2 mb-3">Alertas activas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <AlertCard
            label="Disputas abiertas"
            value={data.alerts.openDisputes}
            severity={data.alerts.openDisputes > 0 ? "warning" : "ok"}
            hint="Liquidaciones en disputa o revisión"
          />
          <AlertCard
            label="Webhooks MP fallidos (7d)"
            value={data.alerts.failedWebhooks7d}
            severity={data.alerts.failedWebhooks7d > 0 ? "danger" : "ok"}
            hint="Audit log con status ≥ 400 o sin respuesta"
          />
          <AlertCard
            label="Tickets con problemas"
            value={data.alerts.ticketsWithIssues}
            severity={data.alerts.ticketsWithIssues > 0 ? "warning" : "ok"}
            hint="RefundPending o Expired"
          />
        </div>
      </section>
    </>
  );
}

function HeroNextEvent({ ev }: { ev: NextEventInfo | null }) {
  if (!ev) {
    return (
      <div className="nx-card p-6">
        <EmptyBlock label="No hay próximos eventos publicados." />
      </div>
    );
  }
  return (
    <section className="nx-card p-6 bg-gradient-to-br from-bg-card to-bg-subtle">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="nx-eyebrow mb-2">Próximo evento</p>
          <h2 className="text-2xl font-bold text-fg tracking-tight mb-2">
            {ev.nombre || "Evento sin nombre"}
          </h2>
          <div className="flex flex-wrap items-center gap-4 text-sm text-fg-muted">
            <span className="flex items-center gap-1.5">
              <MapPin size={14} /> {ev.club || "Sin club"}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} /> {formatEventDate(ev.fecha)}
            </span>
            {ev.hora && (
              <span className="flex items-center gap-1.5">
                <Clock size={14} /> {ev.hora}
              </span>
            )}
          </div>
        </div>
        <Countdown fecha={ev.fecha} hora={ev.hora} />
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Tickets vendidos" value={fmtInt(ev.ticketsSold)} />
        <MiniStat label="Aforo" value={ev.aforo ? fmtInt(ev.aforo) : "—"} />
        <MiniStat
          label="Llenado"
          value={ev.fillRate !== null ? `${ev.fillRate}%` : "—"}
        />
        <MiniStat label="Ingresos" value={fmtCLP(ev.revenue)} />
      </div>

      {ev.fillRate !== null && (
        <div className="mt-3 h-1.5 bg-bg-subtle rounded overflow-hidden">
          <div
            className={`h-full ${
              ev.fillRate >= 80 ? "bg-success" : ev.fillRate >= 40 ? "bg-warning" : "bg-danger"
            }`}
            style={{ width: `${Math.min(100, ev.fillRate)}%` }}
          />
        </div>
      )}
    </section>
  );
}

function Countdown({ fecha, hora }: { fecha: string | null; hora: string | null }) {
  const target = useEventTarget(fecha, hora);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!target) return null;
  const diff = target - now;
  if (diff <= 0) {
    return (
      <div className="text-right">
        <div className="text-2xl font-bold text-success tabular-nums">EN CURSO</div>
        <div className="text-2xs text-fg-subtle">El evento ya empezó</div>
      </div>
    );
  }
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  const text = days > 0
    ? `${days}d ${pad(hours)}:${pad(mins)}:${pad(secs)}`
    : `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  const sub = days > 0 ? `faltan ${days} día${days === 1 ? "" : "s"}` : "restantes";
  return (
    <div className="text-right">
      <div className="text-2xl font-bold text-accent tabular-nums">{text}</div>
      <div className="text-2xs text-fg-subtle">{sub}</div>
    </div>
  );
}

function useEventTarget(fecha: string | null, hora: string | null): number | null {
  if (!fecha) return null;
  const hh = hora || "23:00";
  try {
    return new Date(`${fecha}T${hh}:00`).getTime();
  } catch {
    return null;
  }
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-bg-surface border border-border rounded-md p-3">
      <div className="nx-eyebrow mb-1">{label}</div>
      <div className="text-base font-semibold text-fg tabular-nums">{value}</div>
    </div>
  );
}

function TodayCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="nx-card p-4">
      <div className="flex items-center gap-2 mb-2 text-fg-muted">
        {icon}
        <span className="nx-eyebrow">{label}</span>
      </div>
      <div className="text-2xl font-bold text-fg tabular-nums">{value}</div>
    </div>
  );
}

function AlertCard({
  label,
  value,
  severity,
  hint,
}: {
  label: string;
  value: number;
  severity: "ok" | "warning" | "danger";
  hint: string;
}) {
  const colors = {
    ok: "border-border bg-bg-card",
    warning: "border-warning/40 bg-warning-soft",
    danger: "border-danger/40 bg-danger-soft",
  }[severity];
  const iconColor = {
    ok: "text-success",
    warning: "text-warning",
    danger: "text-danger",
  }[severity];
  const Icon = severity === "ok" ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`nx-card p-4 ${colors}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="nx-eyebrow">{label}</span>
        <Icon size={16} className={iconColor} />
      </div>
      <div className="text-2xl font-bold text-fg tabular-nums">{fmtInt(value)}</div>
      <div className="text-2xs text-fg-subtle mt-1">{hint}</div>
    </div>
  );
}

function ValidationsChart({ data }: { data: OperationalData["validationsByHour"] }) {
  const chartData = data.map((p) => ({
    hour: new Date(p.hour).toLocaleTimeString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    qty: p.qty,
  }));
  return (
    <div className="w-full h-56">
      <ResponsiveContainer>
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--nx-border-subtle)" vertical={false} />
          <XAxis
            dataKey="hour"
            tick={{ fill: "var(--nx-fg-subtle)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: "var(--nx-fg-subtle)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={30}
            allowDecimals={false}
          />
          <Tooltip
            content={({ active, payload, label }: any) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="nx-card p-2 text-xs shadow-lg">
                  <div className="text-fg-subtle">{label}</div>
                  <div className="text-fg font-semibold tabular-nums">
                    {fmtInt(payload[0].value)} validaciones
                  </div>
                </div>
              );
            }}
            cursor={{ fill: "var(--nx-bg-hover)" }}
          />
          <Bar dataKey="qty" fill="var(--nx-success)" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatEventDate(fecha: string | null): string {
  if (!fecha) return "—";
  try {
    return new Date(fecha + "T00:00:00").toLocaleDateString("es-CL", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return fecha;
  }
}
